import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight } from 'lucide-react';

interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  publishDate: string;
  readTime: string;
  category: string;
}

const blogPosts: BlogPost[] = [
  {
    slug: 'best-florists-newtown-same-day',
    title: 'Best Florists in Newtown for Same-Day Flowers',
    excerpt: 'Discover Newtown\'s top florists offering same-day flower delivery. From King Street boutiques to hidden gems.',
    publishDate: '2024-01-15',
    readTime: '5 min',
    category: 'Local Guides'
  },
  {
    slug: 'birthday-flowers-sydney',
    title: 'What Flowers to Send for Birthdays in Sydney',
    excerpt: 'Choose the perfect birthday flowers in Sydney. From classic roses to native Australian blooms.',
    publishDate: '2024-01-20',
    readTime: '6 min',
    category: 'Flower Guides'
  },
  {
    slug: 'local-vs-corporate-florists',
    title: 'Why Local Flower Delivery Beats Big Brands',
    excerpt: 'Discover why ordering from local Sydney florists delivers better value and fresher flowers.',
    publishDate: '2024-01-25',
    readTime: '7 min',
    category: 'Industry Insights'
  },
  {
    slug: 'australian-flower-trends',
    title: 'Top Flower Trends in Australia Right Now',
    excerpt: 'From native blooms to sustainable practices, discover what\'s trending in Australian floristry.',
    publishDate: '2024-02-05',
    readTime: '6 min',
    category: 'Trends'
  },
  {
    slug: 'valentines-day-sydney',
    title: 'Valentine\'s Day Flower Guide for Sydney Buyers',
    excerpt: 'Make Valentine\'s Day special with the perfect flowers. Tips for ordering and what to choose.',
    publishDate: '2024-02-10',
    readTime: '8 min',
    category: 'Holiday Guides'
  },
  {
    slug: 'sympathy-funeral-flowers',
    title: 'Which Flowers Are Best for Funerals or Sympathy?',
    excerpt: 'A sensitive guide to choosing appropriate flowers for funerals and expressing sympathy.',
    publishDate: '2024-02-15',
    readTime: '6 min',
    category: 'Flower Guides'
  }
];

export default function BlogIndex() {
  return (
    <div className="pt-20">
      <Helmet>
        <title>Flower Blog - Tips, Guides & Local Insights | MarketBloom</title>
        <meta name="description" content="Expert flower advice, local florist guides, and seasonal tips from Sydney's flower experts. Discover the best flowers for every occasion and support local florists." />
        <meta name="keywords" content="flower blog, Sydney florists, flower tips, local flower guides, flower care" />
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">MarketBloom Flower Blog</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Expert advice, local insights, and everything you need to know about flowers in Sydney. 
            From choosing the perfect bouquet to supporting local florists, we've got you covered.
          </p>
        </div>

        <div className="grid gap-8 md:gap-12">
          {blogPosts.map((post) => (
            <article key={post.slug} className="border-b border-gray-200 pb-8 last:border-b-0">
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-3">
                <span className="bg-[#967F81] text-white px-3 py-1 rounded-full">{post.category}</span>
                <time dateTime={post.publishDate}>{new Date(post.publishDate).toLocaleDateString('en-AU', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}</time>
                <span>{post.readTime}</span>
              </div>
              
              <h2 className="text-2xl font-bold text-gray-900 mb-3 hover:text-brand-primary transition-colors">
                <Link to={`/blog/${post.slug}`}>
                  {post.title}
                </Link>
              </h2>
              
              <p className="text-gray-700 leading-relaxed mb-4">
                {post.excerpt}
              </p>
              
              <Link 
                to={`/blog/${post.slug}`}
                className="inline-flex items-center text-brand-primary hover:text-[#E63E1C] font-medium transition-colors"
              >
                Read More
                <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </article>
          ))}
        </div>

        <div className="mt-16 text-center">
          <div className="bg-gray-50 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Looking for Fresh Flowers?</h2>
            <p className="text-gray-700 mb-6">
              Browse Sydney's best local florists and discover beautiful arrangements for every occasion.
            </p>
            <Link 
              to="/"
              className="inline-block bg-[#967F81] text-white px-8 py-3 rounded-lg font-medium hover:bg-[#E63E1C] transition-colors"
            >
              Shop Flowers Now
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
} 